// Pure display helper for the sales rep (BL-752, repointed to the `sales_rep`
// roster by BL-877) — turns a joined `sales_rep` row into the string the UI
// renders.
//
// BL-877 changed the source table from `staff_users` (a single `name` column)
// to `sales_rep` (separate `first_name` / `last_name`), so the input shape
// carries the two parts and this helper owns the one place they are joined.
// Assembling a rep's display name at a call site is banned — every surface
// (customer list/detail, lot panel, rings, inventory, settlement derivation)
// routes through here so "how an archived rep reads" can never drift.

/**
 * The marker after an archived rep's name — the one literal for it (BUG-2129):
 * every rep display reads it through `formatSalesRepName`, and the lot Contacts
 * section appends it to a snapshot row whose rep was archived.
 */
export const SALES_REP_ARCHIVED_SUFFIX = ' (Archived)';

/** The name a rep with neither name part renders as (also the rep sort's key for it, BL-2011). */
export const UNNAMED_SALES_REP_LABEL = 'Unnamed rep';

/** Minimum `sales_rep` row shape the formatter needs: name parts + soft-delete marker. */
export interface SalesRepNameInput {
  /** `sales_rep.first_name`. Nullable — the roster only requires a tenant. */
  firstName: string | null;
  /** `sales_rep.last_name`. Nullable — see `firstName`. */
  lastName: string | null;
  /** `sales_rep.deleted_at` — non-null means the rep is soft-archived (ADR 018). */
  deletedAt: string | Date | null;
}

/**
 * Format a sales rep's display name from a joined `sales_rep` row.
 *
 * Pure IsomorphicService-style helper (no I/O, no side effects) shared by every
 * read path that displays a rep, server and client alike.
 *
 * A rep with neither name part is rendered as `'Unnamed rep'` rather than an
 * empty string, so an archived nameless row still reads as
 * `'Unnamed rep (Archived)'` instead of a bare `' (Archived)'`. Callers that
 * want a "no rep assigned" sentinel (`'House'`, `'Unassigned'`) pass `null` and
 * apply their own — that choice is per-surface and deliberately not made here.
 *
 * @param input - The joined roster row, or null/undefined when nothing is assigned.
 * @returns The rep's full name for an active rep, `'<name> (Archived)'` when the
 *   rep is soft-deleted, or `null` when there is no rep.
 */
export function formatSalesRepName(
  input: SalesRepNameInput | null | undefined,
): string | null {
  if (input == null) return null;

  const name = [input.firstName, input.lastName]
    .map((part) => part?.trim())
    .filter((part): part is string => !!part)
    .join(' ');

  const display = name || UNNAMED_SALES_REP_LABEL;

  return input.deletedAt != null ? `${display}${SALES_REP_ARCHIVED_SUFFIX}` : display;
}
